import { useState, useEffect, useRef } from "react";
import { Info, HelpCircle } from "lucide-react";

/* Shared ApexCharts config for subtle depth. Kept intentionally minimal so
   every dashboard tab renders with the same familiar style. */
export const CHART_3D = {
  chart: {
    toolbar: { show: false },
    fontFamily: "'DM Sans', sans-serif",
    dropShadow: { enabled: true, top: 2, left: 0, blur: 4, opacity: 0.08 },
  },
  theme: {
    mode: 'light',
    palette: 'palette1',
    monochrome: { enabled: false },
  },
  grid: { borderColor: "#F0F1F5", strokeDashArray: 4 },
  dataLabels: { enabled: false },
  fill: {
    type: "gradient",
    gradient: { shade: "light", type: "vertical", shadeIntensity: 0.15, opacityFrom: 0.95, opacityTo: 0.85 },
  },
  plotOptions: {
    bar: { borderRadius: 5, columnWidth: "55%", distributed: false },
  },
  states: {
    hover: { filter: { type: "darken", value: 0.08 } },
    active: { filter: { type: "darken", value: 0.12 } },
  },
};

export function mergeChart(...configs) {
  const result = {};
  for (const config of configs) {
    for (const key of Object.keys(config)) {
      if (typeof config[key] === "object" && config[key] !== null && !Array.isArray(config[key])) {
        result[key] = { ...(result[key] || {}), ...config[key] };
        // Deep merge for nested objects (chart, plotOptions, etc.)
        if (typeof (result[key]) === "object") {
          for (const subKey of Object.keys(config[key])) {
            if (typeof config[key][subKey] === "object" && config[key][subKey] !== null && !Array.isArray(config[key][subKey])) {
              result[key][subKey] = { ...((result[key] || {})[subKey] || {}), ...config[key][subKey] };
            }
          }
        }
      } else {
        result[key] = config[key];
      }
    }
  }
  return result;
}

/* ─── Animated Circle ─── */
export function AnimatedCircle({ value, color, size = 64, strokeWidth = 3, delay = 0 }) {
  const [displayValue, setDisplayValue] = useState(0);
  const [dashValue, setDashValue] = useState(0);
  const circumference = 2 * Math.PI * ((size / 2) - strokeWidth);
  const targetDash = (value / 100) * circumference;

  useEffect(() => {
    const timer = setTimeout(() => {
      const duration = 800;
      const startTime = performance.now();
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplayValue(Math.round(eased * value));
        setDashValue(eased * targetDash);
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }, delay);
    return () => clearTimeout(timer);
  }, [value, targetDash, delay]);

  const r = (size / 2) - strokeWidth;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#EEF0F4" strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={`${dashValue} ${circumference}`} strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.1s ease" }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-mono text-[13px] font-bold" style={{ color }}>
        {displayValue}%
      </span>
    </div>
  );
}

/* ─── Animated Progress Bar ─── */
export function AnimatedProgress({ value, color, height = 8, delay = 0 }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setWidth(value), delay + 100);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return (
    <div className="w-full rounded-full overflow-hidden" style={{ height, backgroundColor: "#EEF0F4" }}>
      <div className="rounded-full" style={{
        height, backgroundColor: color, width: `${width}%`,
        transition: "width 0.8s cubic-bezier(0.22, 1, 0.36, 1)",
        boxShadow: `0 1px 3px ${color}40`,
      }} />
    </div>
  );
}

/* ─── Data Source Badge (with real logos) ─── */
export function DataSourceBadge({ source, className = "", compact = false }) {
  const sources = {
    quickbooks: { label: "QuickBooks", bg: "#FEF3E6", color: "#A16207", logo: "/images/integrations/quickbooks.png" },
    xero: { label: "Xero", bg: "#E0F2FE", color: "#0369A1", logo: "/images/integrations/xero.svg" },
    plaid: { label: "Plaid", bg: "#EFF6FF", color: "#1B75BA", logo: "/images/integrations/plaid.png" },
    hubspot: { label: "HubSpot", bg: "#FFF4E0", color: "#D97706", logo: "/images/integrations/hubspot.svg" },
    salesforce: { label: "Salesforce", bg: "#E0F2FE", color: "#1B75BA", logo: "/images/integrations/salesforce.png" },
    google_sheets: { label: "Google Sheets", bg: "#FEF3E6", color: "#D4A017", logo: "/images/integrations/google_sheets.png" },
    manual: { label: "Manual Entry", bg: "#F3F4F8", color: "#6B7491", icon: "ME" },
    demo: { label: "Demo Data", bg: "#F3F4F8", color: "#6B7491", icon: "DM" },
    google_news: { label: "Google News", bg: "#E0F2FE", color: "#1B75BA", icon: "GN" },
    bloomberg: { label: "Bloomberg", bg: "#1C2040", color: "#FFFFFF", icon: "BB" },
  };
  const s = sources[source] || sources.demo;
  if (compact) {
    return (
      <span className={`inline-flex items-center justify-center rounded-full ${className}`}
        style={{ width: 18, height: 18, backgroundColor: s.bg }}
        data-testid={`source-badge-${source}`}
        title={s.label}
        aria-label={s.label}>
        {s.logo ? (
          <img src={s.logo} alt={s.label} className="h-2.5 w-2.5 object-contain" />
        ) : (
          <span className="text-[7px] font-bold" style={{ color: s.color }}>{s.icon}</span>
        )}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold ${className}`}
      style={{ backgroundColor: s.bg, color: s.color }} data-testid={`source-badge-${source}`}>
      {s.logo ? (
        <img src={s.logo} alt={s.label} className="h-3 w-3 object-contain" />
      ) : (
        <span className="font-mono">{s.icon}</span>
      )}
      {s.label}
    </span>
  );
}

/* ─── InfoTooltip (i) ─── */
export function InfoTooltip({ text, className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div className={`relative inline-flex ${className}`} ref={ref}>
      <button onClick={() => setOpen(!open)}
        className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-blue-50 transition-colors"
        style={{ color: "#9BA4B8" }} data-testid="info-tooltip-trigger" aria-label="Show calculation info">
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 rounded-lg text-xs leading-relaxed shadow-xl"
          style={{ backgroundColor: "#1C2040", color: "#E9ECF2", border: "1px solid #3D4468" }}
          data-testid="info-tooltip-content">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
            <div className="w-2 h-2 rotate-45" style={{ backgroundColor: "#1C2040" }} />
          </div>
          {text}
        </div>
      )}
    </div>
  );
}

/* ─── Graph Explainer (?) - beautiful help icon for every chart ─── */
export function GraphExplainer({ title, text, className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div className={`relative inline-flex ${className}`} ref={ref}>
      <button onClick={() => setOpen(!open)}
        className="w-5 h-5 rounded-full flex items-center justify-center transition-all"
        style={{
          background: open ? "linear-gradient(135deg, #1B75BA, #282560)" : "#F0F1F5",
          color: open ? "#FFFFFF" : "#9BA4B8",
          boxShadow: open ? "0 2px 8px rgba(27,117,186,0.25)" : "none",
        }}
        data-testid="graph-explainer-trigger" aria-label={`What is ${title}?`}>
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute z-50 bottom-full right-0 mb-2.5 w-80 rounded-xl shadow-2xl overflow-hidden"
          style={{ border: "1px solid #E8EAF0" }}
          data-testid="graph-explainer-content">
          <div className="px-4 py-2.5" style={{ background: "linear-gradient(135deg, #1B75BA, #282560)" }}>
            <p className="text-[11px] font-bold text-white font-display">{title}</p>
          </div>
          <div className="px-4 py-3 bg-white">
            <p className="text-xs leading-relaxed" style={{ color: "#3A3F5C" }}>{text}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Empty State ─── */
export function EmptyState({ title, description, integrationLink = "/integrations" }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-state">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "#EFF6FF" }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1B75BA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </div>
      <h3 className="text-base font-bold font-display mb-1" style={{ color: "#1C2040" }}>{title || "No Data Available"}</h3>
      <p className="text-sm max-w-sm mb-4" style={{ color: "#6B7491" }}>
        {description || "Connect an integration to start seeing real-time data from your accounts."}
      </p>
      <a href={integrationLink} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:shadow-lg"
        style={{ backgroundColor: "#1B75BA" }} data-testid="connect-integration-btn">
        Connect Integration
      </a>
    </div>
  );
}

/* ─── FadeInCard ─── */
export function FadeInCard({ children, delay = 0, className = "", style = {}, ...props }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div className={className} style={{
      ...style,
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(8px)",
      transition: "opacity 0.4s ease-out, transform 0.4s ease-out",
    }} {...props}>
      {children}
    </div>
  );
}
